import React, { useRef, useState } from 'react';
import { Button, Row, Col } from 'antd';

import { getFullName } from 'utils/personUtils';
import { checkIsCompanyOwner } from 'utils/userRolesUtils';
import BaseAlert from 'components/wrappers/BaseAlert';
import EditBillingInfoModal from './EditBillingInfoModal';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import BasePageCard from 'components/wrappers/BasePageCard';
import { ProfileCorporateInfoModel } from 'api/backofficeApiClient';
import { getBillingInformation } from 'redux/reducers/accountSlice';
import BaseSectionMessage from 'components/wrappers/BaseSectionMessage';
import { userSelector } from 'redux/selectors/authenticationSelectors';
import useScrollAfterRedirect from 'features/account/hooks/useScrollAfterRedirect';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './BillingInfo.module.less';

type Props = {
  corporateInfo?: ProfileCorporateInfoModel;
};

export default function BillingInfo(props: Props) {
  const { corporateInfo } = props;
  const dispatch = useAppDispatch();
  const user = useAppSelector(userSelector);
  const isCompanyOwner = checkIsCompanyOwner(user);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [billingInfo, setBillingInfo] = useState(corporateInfo?.billingInfo);
  const sectionRef = useRef<HTMLDivElement>(null);
  useScrollAfterRedirect({ sectionRef });

  const billingInfoName = getFullName(
    billingInfo?.firstName,
    billingInfo?.lastName,
  );

  const loadBillingInfo = async () => {
    const result = await dispatch(getBillingInformation());

    if (getBillingInformation.fulfilled.match(result)) {
      setBillingInfo(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleOpenModal = () => {
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  return (
    <>
      {isModalVisible && (
        <EditBillingInfoModal
          onEdit={loadBillingInfo}
          billingInfo={billingInfo}
          onClose={handleCloseModal}
        />
      )}

      <BasePageCard
        ref={sectionRef}
        title="Billing information"
        className="stretch-card"
        icons={<span className="icon icon-invoice"></span>}
        subTitle={
          isCompanyOwner &&
          !!billingInfo && (
            <Button
              onClick={handleOpenModal}
              className="with-icon mobile-icon-only"
              size="large"
              icon={<span className="icon icon-edit" />}
              data-id="openBillingInfoModal"
            >
              Edit
            </Button>
          )
        }
      >
        <div className="content-wrapper scroll-box">
          {!billingInfo && isCompanyOwner && (
            <BaseSectionMessage
              type="info"
              title="Info"
              message={
                <div className={styles.message}>
                  <span>
                    Please update Billing information in order to create new
                    assignment.
                  </span>
                  <Button
                    className={styles.button}
                    onClick={handleOpenModal}
                    type="link"
                  >
                    Fill in now.
                  </Button>
                </div>
              }
            />
          )}

          {!!billingInfo && (
            <Row gutter={[4, 4]} className="titled-sheet">
              {!!billingInfoName && (
                <Col xs={24} md={12}>
                  <div className="title">A/P:</div>
                  <div>{billingInfoName}</div>
                </Col>
              )}

              {!!billingInfo.email && (
                <Col xs={24} md={12}>
                  <div className="title">A/P email:</div>
                  <div>{billingInfo.email}</div>
                </Col>
              )}

              {!!billingInfo.phoneNumber && (
                <Col xs={24} md={12}>
                  <div className="title">A/P phone number:</div>
                  <div>{billingInfo.phoneNumber}</div>
                </Col>
              )}
            </Row>
          )}
        </div>
      </BasePageCard>
    </>
  );
}
